import { Decimal } from 'decimal.js'

export const vn_getEntries = (obj) => {
  if (!Object.entries) {
    return Object.keys(obj).map((key) => [key, obj[key]])
  } else {
    return Object.entries(obj)
  }
}

/**
 * Calculate the distribution of payments into an N number of buckets.
 * @param {int} amount The amount to distribute
 * @param {int} total The entire value of the payment
 * @param {int} count How many distributions need to happen
 * @returns {Array} Returns an array of all the distributions, may not be even.
 */
export const calculateDistributionOfPayment = (amount, total, count) => {
  let distributedAmount = 0
  const distributedAmounts = []

  // First distribute by weight with a possible remainder. Simultaneously aggregate the possible
  // previous tax amount of items which are now removed.
  for (let i = 0; i < count; i++) {
    const distribution = Math.floor((amount * total) / total)
    distributedAmount += distribution
    distributedAmounts[i] = distribution
  }

  // Next distribute what hasn't been distributed unless that setting has been turned off.
  let undistributedAmount = amount - distributedAmount

  const additive = undistributedAmount > 0 ? 1 : -1
  undistributedAmount *= additive
  let objectNumber = 0

  while (undistributedAmount > 0) {
    distributedAmounts[objectNumber] += additive
    undistributedAmount -= 1
    objectNumber = (objectNumber + 1) % count
  }

  return distributedAmounts
}

/**
 * Checks to see if an object is empty
 * @param {Object} - The object to check
 * @returns {Boolean} - Returns true if empty, false otherwise
 */
export const isEmpty = (value) => {
  // check for falsy values
  if (!value) {
    return true
  }

  if (value.constructor === Object) {
    if (vn_getEntries(value).length === 0) {
      return true
    }
  }

  return false
}

/**
 * given a JWT, this will parse and return it
 * @param {String} token - A JWT token
 * @returns {Object} A valid JSON object if it can be parsed, null otherwise
 */
export const parseJWT = (token) => {
  try {
    return JSON.parse(atob(token.split('.')[1]))
  } catch (e) {
    return null
  }
}

/**
 * Checks to see if the value has already been applied on the order
 * @param {Object} order - The current order
 * @param {String} code - The ticket to check
 * @returns {Boolean} - Returns true if the ticket has already been used, false otherwise
 */
export const codeAlreadyUsed = (order, code) => {
  if (code) {
    for (let i = 0; i < order?.payments?.length; i++) {
      const payment = order?.payments[i]

      if (payment.ticketId === code || payment.qrCode === code || payment.token === code) {
        return true
      }
    }
  }

  return false
}

// not ready yet
/**
 * Equivalent to Lodash' `reduce`. Supports both arrays and objects.
 *
 * @param {Array|Object} collection
 * @param {Function} iterator
 * @param {*} [accumulator] - initial value.
 * @returns {Array|Object}
 */
export const reduce = (collection, iterator, accumulator) => {
  if (Array.isArray(collection)) {
    return collection.reduce(iterator, accumulator)
  }

  if (typeof collection === 'object' && collection !== null) {
    return Object.keys(collection).reduce((acc, key) => {
      return iterator(acc, collection[key], key, collection)
    }, accumulator)
  }

  throw new TypeError('Expected an array or object')
}

export const getValidPayments = (payments = []) => {
  if (!Array.isArray(payments)) return []
  if (payments.length < 2) return payments
  return payments?.filter((payment) => payment.amountInCents !== 0)
}

/**
 * Takes a monetary value in float format and converts to cents
 * @param {String || Float} value - The value to convert.
 * @param {Boolean} returnAsString - Should this be returned as a string or not, default: false
 * @returns {Float || String} Returns the new value as either float or string.
 */
export const floatToCents = (value, returnAsString = false) => {
  // if you pass in nothing, lets just return 0
  if (!value) {
    return 0
  }

  // Parse the decimal string
  const amount = new Decimal(value)

  // Multiply by 100 to convert to cents and convert to integer
  const cents = amount.times(100).toDecimalPlaces(0, Decimal.ROUND_DOWN)

  // Return cents as a string
  if (returnAsString) {
    return cents.toString()
  }

  // Return cents as an integer
  return cents.toNumber()
}

/**
 * Checks if a hex color is too light based on a brightness threshold.
 *
 * @param {*} hex - The hex color code (e.g., "#FFFFFF").
 * @param {*} threshold - The brightness threshold (default: 200).
 * @returns {Boolean} - Returns true if the color is too light, false otherwise.
 */
export const isColorTooLight = (hex, threshold = 200) => {
  if (!hex) return false
  let c = hex.replace('#', '')
  if (c.length === 3) {
    c = c
      .split('')
      .map((x) => x + x)
      .join('')
  }
  const r = parseInt(c.substr(0, 2), 16)
  const g = parseInt(c.substr(2, 2), 16)
  const b = parseInt(c.substr(4, 2), 16)
  const brightness = (r * 299 + g * 587 + b * 114) / 1000

  return brightness > threshold
}
